import { API_BASE } from '../env'
import { ApiError, request } from './http'
import { getAccessToken, getStepUpHandler } from '../store/authStore'
import type { Schema } from './schema'

/**
 * Per-brand access as `/api/me` returns it.
 *
 * Hand-written rather than `Schema<'BrandAccessWire'>` on purpose: today's relay sends every flag,
 * but `derivePermissions` also reads relays older than `permissions` on `/api/me`, which sent some
 * flags or none, so every member stays optional here.
 */
export interface BrandAccessWire {
  brand_id: string
  brand_name: string
  role_name: string
  permissions?: {
    can_view_devices?: boolean
    can_connect_vnc?: boolean
    can_connect_ssh?: boolean
    can_manage_devices?: boolean
    can_manage_users?: boolean
  }
}

/**
 * - `permissions`: Added server-side as part of this rewrite. Absent when talking to a relay that
 *   predates it, which is why `derivePermissions` still exists in the permissions context.
 */
export type MeWire = Schema<'MeWire'>

// ── Identity ─────────────────────────────────────────────────────────────────────────────────────

/**
 * The permission authority.
 *
 * Relay's permissions are read from here rather than from the token: the identity provider's token
 * does not carry them, and putting them there would inflate a token that also travels in a WebSocket
 * URL.
 */
export function getMe(token?: string | null): Promise<MeWire> {
  return request<MeWire>('GET', `${API_BASE}/me`, { token })
}

// ── Signing in ───────────────────────────────────────────────────────────────────────────────────

/** Where people sign in, and whether step-up is in force (`GET /api/auth/config`, anonymous). */
export type AuthConfigWire = Schema<'AuthConfigWire'>

export function getAuthConfig(): Promise<AuthConfigWire> {
  return request<AuthConfigWire>('GET', `${API_BASE}/auth/config`)
}

let config: Promise<AuthConfigWire> | null = null

/** Forgets the configuration asked for, so the next use asks again. For tests. */
export function forgetAuthConfig(): void {
  config = null
}

/** The configuration, asked for once per page. */
export function authConfig(): Promise<AuthConfigWire> {
  config ??= getAuthConfig().catch((error: unknown) => {
    config = null
    throw error
  })
  return config
}

/**
 * Whether the caller's sign-in is recent and strong enough for `action` (step-up). Resolves when it
 * is; rejects with the relay's `step_up_required` refusal when it isn't.
 */
export function checkStepUp(action: string, deviceId?: string): Promise<unknown> {
  if (deviceId)
    return request<unknown>('GET', `${API_BASE}/auth/step-up/${encodeURIComponent(action)}?device=${encodeURIComponent(deviceId)}`, {
      stepUpRetry: false,
    })
  return request<unknown>('GET', `${API_BASE}/auth/step-up/${encodeURIComponent(action)}`, { stepUpRetry: false })
}

/**
 * The token to open a viewer session with, after making sure the sign-in is recent and strong enough for
 * `action` where the relay requires it (step-up). A browser can't read why a WebSocket handshake was refused,
 * so this asks first and, if refused, signs in again before the socket is opened.
 */
export async function sessionToken(action: 'remote_control' | 'terminal', deviceId?: string): Promise<string | null> {
  try {
    const { step_up: stepUp } = await authConfig()
    // Step-up relay-wide, or a brand that asks for a fresh code before remote control of its devices
    // (Plans/sign-in-remembered-browsers.md): the device decides which.
    if ((stepUp.enabled && stepUp.actions.includes(action)) || stepUp.brand_actions?.includes(action))
      await checkStepUp(action, deviceId)
  } catch (error) {
    const stepUp = getStepUpHandler()
    if (error instanceof ApiError && error.code === 'step_up_required' && stepUp) await stepUp(error.acrValues)
    // Anything else (the relay unreachable): the session's own connection will say so.
  }
  return getAccessToken()
}
